/**
 * FoundingMemberCTA — Honest "we're new, join our first 100 families" CTA.
 *
 * Subscribes to Firestore `config/foundingCount` (publicly readable) and
 * shows a live counter of plasser tatt / spots taken. Aggregation runs
 * server-side every 10 min + on each relevant write — see
 * `firebase/functions/src/email/foundingMemberCounter.ts`.
 *
 * States:
 *   count <  90  →  "X av 100 plasser tatt"           (normal)
 *   count >= 90  →  "Bare {100-N} plasser igjen!"     (urgency)
 *   count >= 100 →  "100 founding families nådd"      (cap reached)
 *                   → CTA flips to standard signup
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase/config';
import { trackEvent } from '../../lib/analytics';

export interface FoundingMemberCTAProps {
  lang: 'no' | 'en';
}

const FOUNDING_CAP = 100;

export function FoundingMemberCTA({ lang }: FoundingMemberCTAProps) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!isFirebaseConfigured() || !db) return;
    const unsub = onSnapshot(
      doc(db, 'config', 'foundingCount'),
      (snap) => {
        const data = snap.data() as { count?: number } | undefined;
        if (data && typeof data.count === 'number') {
          setCount(Math.min(data.count, FOUNDING_CAP));
        }
      },
      // Failure mode: stay in the "hardcoded fallback" state silently.
      () => {}
    );
    return () => unsub();
  }, []);

  // Derive a safe display count + remaining seats.
  const taken = count ?? 0;
  const remaining = Math.max(FOUNDING_CAP - taken, 0);
  const capReached = taken >= FOUNDING_CAP;
  const urgent = !capReached && remaining <= 10;

  const copy = lang === 'no'
    ? {
        eyebrow: 'Founding 100',
        heading: capReached ? '100 founding families nådd.' : 'Good Atoms er nytt.',
        body: capReached
          ? 'Takk til alle som ble med først. Du kan fortsatt prøve Good Atoms som ordinært medlem — første måned er gratis.'
          : 'Bli en av våre første 100 familier — gratis i 6 måneder, founding-member-badge for alltid.',
        cta: capReached ? 'Prøv Good Atoms gratis' : 'Bli founding member',
        counter: capReached
          ? '100 av 100 plasser tatt'
          : urgent
            ? `Bare ${remaining} plass${remaining === 1 ? '' : 'er'} igjen!`
            : count === null
              ? 'Færre enn 100 plasser igjen'
              : `${taken} av 100 plasser tatt`,
      }
    : {
        eyebrow: 'Founding 100',
        heading: capReached ? 'All 100 founding spots taken.' : 'Good Atoms is new.',
        body: capReached
          ? 'Thanks to everyone who came in first. You can still try Good Atoms as a regular member — first month free.'
          : 'Become one of our first 100 families — free for 6 months, founding-member badge forever.',
        cta: capReached ? 'Try Good Atoms free' : 'Become a founding member',
        counter: capReached
          ? '100 of 100 spots taken'
          : urgent
            ? `Only ${remaining} spot${remaining === 1 ? '' : 's'} left!`
            : count === null
              ? 'Fewer than 100 spots remaining'
              : `${taken} of 100 spots taken`,
      };

  // No utm_* params on internal links — GA4 treats them as a new session and
  // overwrites the real acquisition source (google/organic, facebook, …),
  // which makes channel attribution for subscribers impossible. The campaign
  // is tracked via the cta_click event + a plain ref param instead.
  const campaign = capReached ? 'standard_member' : 'founding_100';
  const ctaHref = `/login?signup=1&ref=${campaign}`;

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="max-w-3xl mx-auto mt-16 mb-8 p-8 md:p-10 rounded-2xl border border-teal/20 text-center"
      style={{ backgroundColor: '#F5EDD8' }}
    >
      <p
        className="inline-block font-display font-semibold text-xs uppercase tracking-widest mb-3 px-3 py-1 rounded-full text-white"
        style={{ backgroundColor: '#00C2A8', letterSpacing: '0.12em' }}
      >
        {copy.eyebrow}
      </p>
      <h3 className="font-display font-bold text-ink text-2xl md:text-3xl mb-3">
        {copy.heading}
      </h3>
      <p className="font-body text-ink-muted text-lg mb-6 max-w-xl mx-auto">
        {copy.body}
      </p>
      <Link
        to={ctaHref}
        onClick={() => trackEvent('cta_click', { cta: campaign, location: 'blog_post' })}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal text-white font-display font-semibold text-base hover:bg-teal-dark transition-colors shadow-glow hover:shadow-glow-lg"
        style={{ backgroundColor: '#00C2A8' }}
      >
        {copy.cta}
        <span aria-hidden="true">→</span>
      </Link>
      <p
        className={`mt-4 font-display text-sm ${urgent ? 'text-ink font-semibold' : 'text-ink-muted'}`}
        aria-live="polite"
      >
        {copy.counter}
      </p>
    </motion.section>
  );
}

export default FoundingMemberCTA;
